// pages/UserManagement/AllUsers.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Stack,
  Switch,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Category as SpeciesIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SupervisorIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { getCurrentUser, getAuthToken } from "../../utils/AuthHelper";

// IMPORTANT: Set a fallback value for API_BASE_URL
const API_BASE_URL = "https://api.tpilums.org.pk";

console.log("API_BASE_URL:", API_BASE_URL);

export default function AllUsers() {
  const user = getCurrentUser();
  const token = getAuthToken();
  const isMounted = useRef(true);

  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // State for pagination data
  const [totalUsers, setTotalUsers] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [orgFilter, setOrgFilter] = useState("all");

  // Local pagination for frontend table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    user_type: "",
    organization: "",
    is_active: true,
  });
  const [editLoading, setEditLoading] = useState(false);

  // Permissions Dialog - For regular users (UserSpeciesPermission)
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [userForPermissions, setUserForPermissions] = useState(null);
  const [availableSpecies, setAvailableSpecies] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Organization Permissions Dialog - For organization admins (OrganizationSpeciesPermission)
  const [orgPermissionsDialogOpen, setOrgPermissionsDialogOpen] =
    useState(false);
  const [orgForPermissions, setOrgForPermissions] = useState(null);
  const [availableOrgSpecies, setAvailableOrgSpecies] = useState([]);
  const [selectedOrgSpecies, setSelectedOrgSpecies] = useState([]);
  const [orgPermissionsLoading, setOrgPermissionsLoading] = useState(false);

  // Set up isMounted ref
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchUsers = async (url = null) => {
    try {
      const headers = { Authorization: `Token ${token}` };

      let usersRes;

      if (url) {
        // Fetch specific page
        console.log("Fetching from URL:", url);
        const response = await fetch(url, { headers });
        console.log("Response status:", response.status);

        if (!response.ok) {
          const text = await response.text();
          console.error("Response body:", text.substring(0, 500));
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        usersRes = await response.json();
        return usersRes;
      } else if (user?.user_type === "admin") {
        // System admin can see all users - try without page_size first
        const url = `${API_BASE_URL}/accounts/api/users/`;
        console.log("Fetching users from:", url);

        const response = await fetch(url, { headers });
        console.log("Response status:", response.status);

        if (!response.ok) {
          const text = await response.text();
          console.error("Response body:", text.substring(0, 500));
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        usersRes = await response.json();
        console.log("Users response:", usersRes);
      } else if (user?.user_type === "superuser") {
        // Organization admin can only see users in their organization
        const response = await fetch(
          `${API_BASE_URL}/accounts/api/users/my_organization_users/`,
          { headers },
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        usersRes = await response.json();
      } else {
        // Regular users shouldn't access this page
        return { results: [], count: 0, next: null, previous: null };
      }

      // Handle both paginated and non-paginated responses
      const results = usersRes.results || usersRes;
      const count =
        usersRes.count || (Array.isArray(results) ? results.length : 0);
      const next = usersRes.next || null;
      const previous = usersRes.previous || null;

      return {
        results: Array.isArray(results) ? results : [],
        count: count,
        next: next,
        previous: previous,
      };
    } catch (err) {
      console.error("Error fetching users:", err);
      throw err;
    }
  };

  // Function to fetch all data
  const fetchData = async (pageUrl = null) => {
    if (!isMounted.current) return;

    setLoading(true);
    try {
      const headers = { Authorization: `Token ${token}` };

      // Fetch users with pagination
      const usersData = await fetchUsers(pageUrl);

      if (isMounted.current) {
        setUsers(usersData.results || []);
        setTotalUsers(usersData.count || 0);
        setNextPage(usersData.next);
        setPreviousPage(usersData.previous);
      }

      // Fetch organizations (admin only)
      if (user?.user_type === "admin") {
        const orgsRes = await fetch(
          `${API_BASE_URL}/accounts/api/organizations/`,
          { headers },
        );
        if (orgsRes.ok) {
          const orgsData = await orgsRes.json();
          if (isMounted.current) {
            setOrganizations(orgsData.results || orgsData || []);
          }
        }
      }

      // Fetch ALL species for system admin (for assigning to organizations)
      if (user?.user_type === "admin") {
        const speciesRes = await fetch(`${API_BASE_URL}/core/api/specie/`, {
          headers,
        });
        if (speciesRes.ok) {
          const speciesData = await speciesRes.json();
          if (isMounted.current) {
            const speciesList = (speciesData.results || speciesData || []).map(
              (s) => ({
                ...s,
                id: s.id,
              }),
            );
            setAvailableOrgSpecies(speciesList);
          }
        }
      }

      // Fetch species for organization admin (for assigning to regular users)
      if (user?.user_type === "superuser" && user?.organization?.id) {
        // Get species that this organization has permission for
        const orgSpeciesRes = await fetch(
          `${API_BASE_URL}/core/api/organization-species-permissions/?organization=${user.organization.id}`,
          { headers },
        );

        if (orgSpeciesRes.ok) {
          const permData = await orgSpeciesRes.json();
          const permissions = permData.results || permData || [];

          // Get full species details for each permission
          const speciesPromises = permissions.map(async (perm) => {
            const specieId =
              typeof perm.specie === "object" ? perm.specie.id : perm.specie;
            try {
              const specieRes = await fetch(
                `${API_BASE_URL}/core/api/specie/${specieId}/`,
                { headers },
              );
              if (specieRes.ok) {
                return await specieRes.json();
              }
            } catch (err) {
              console.error(`Error fetching species ${specieId}:`, err);
            }
            return null;
          });

          const speciesList = (await Promise.all(speciesPromises)).filter(
            (s) => s !== null,
          );

          if (isMounted.current) {
            setAvailableSpecies(speciesList);
          }
        }
      }

      if (isMounted.current) {
        setLoading(false);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      if (isMounted.current) {
        setError("Failed to load data. Please try again.");
        setLoading(false);
      }
    }
  };

  // Use useEffect with empty dependency array to fetch data only once
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = users.filter((userItem) => {
    const matchesSearch =
      (userItem.username?.toLowerCase() || "").includes(
        searchTerm.toLowerCase(),
      ) ||
      (userItem.email?.toLowerCase() || "").includes(
        searchTerm.toLowerCase(),
      ) ||
      (userItem.first_name?.toLowerCase() || "").includes(
        searchTerm.toLowerCase(),
      ) ||
      (userItem.last_name?.toLowerCase() || "").includes(
        searchTerm.toLowerCase(),
      );

    const matchesType =
      userTypeFilter === "all" || userItem.user_type === userTypeFilter;

    const matchesOrg =
      orgFilter === "all" ||
      (userItem.organization &&
        (typeof userItem.organization === "object"
          ? userItem.organization.id === parseInt(orgFilter)
          : userItem.organization === parseInt(orgFilter)));

    return matchesSearch && matchesType && matchesOrg;
  });

  const getUserTypeIcon = (type) => {
    switch (type) {
      case "admin":
        return <AdminIcon />;
      case "superuser":
        return <SupervisorIcon />;
      case "user":
        return <PersonIcon />;
      case "viewer":
        return <VisibilityIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getUserTypeColor = (type) => {
    switch (type) {
      case "admin":
        return "error";
      case "superuser":
        return "warning";
      case "user":
        return "success";
      case "viewer":
        return "info";
      default:
        return "default";
    }
  };

  const handleOpenEditDialog = (userItem) => {
    setSelectedUser(userItem);
    setEditForm({
      username: userItem.username || "",
      email: userItem.email || "",
      first_name: userItem.first_name || "",
      last_name: userItem.last_name || "",
      user_type: userItem.user_type || "",
      organization: userItem.organization?.id || "",
      is_active: userItem.is_active ?? true,
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setEditForm({
      username: "",
      email: "",
      first_name: "",
      last_name: "",
      user_type: "",
      organization: "",
      is_active: true,
    });
  };

  const handleEditSubmit = async () => {
    setEditLoading(true);
    try {
      const headers = {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      };

      const payload = {
        username: editForm.username,
        email: editForm.email,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        user_type: editForm.user_type,
        is_active: editForm.is_active,
      };

      // Only admin can change organization
      if (user?.user_type === "admin" && editForm.organization) {
        payload.organization = editForm.organization;
      }

      const response = await fetch(
        `${API_BASE_URL}/accounts/api/users/${selectedUser.id}/`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update user");
      }

      setSuccess("User updated successfully");

      // Refresh data
      await fetchData();

      handleCloseEditDialog();
    } catch (err) {
      console.error("Error updating user:", err);
      setError(err.message || "Failed to update user");
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleUserStatus = async (userItem) => {
    try {
      const headers = {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      };

      const payload = {
        is_active: !userItem.is_active,
      };

      const response = await fetch(
        `${API_BASE_URL}/accounts/api/users/${userItem.id}/`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update user status");
      }

      setSuccess(
        `User ${userItem.is_active ? "deactivated" : "activated"} successfully`,
      );

      // Refresh data
      await fetchData();
    } catch (err) {
      console.error("Error toggling user status:", err);
      setError("Failed to update user status");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      const headers = { Authorization: `Token ${token}` };

      const response = await fetch(
        `${API_BASE_URL}/accounts/api/users/${userId}/`,
        {
          method: "DELETE",
          headers,
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete user");
      }

      setSuccess("User deleted successfully");

      // Refresh data
      await fetchData();
    } catch (err) {
      console.error("Error deleting user:", err);
      setError("Failed to delete user");
    }
  };

  // Handle User Species Permissions (for regular users)
  const handleOpenUserPermissionsDialog = async (userItem) => {
    setUserForPermissions(userItem);
    setPermissionsLoading(true);

    try {
      const headers = { Authorization: `Token ${token}` };

      console.log(
        `Opening permissions dialog for user: ${userItem.id} - ${userItem.username}`,
      );

      // Fetch current permissions for THIS SPECIFIC USER ONLY
      const response = await fetch(
        `${API_BASE_URL}/core/api/user-species-permissions/?user=${userItem.id}`,
        { headers },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch permissions");
      }

      const data = await response.json();
      const permissions = data.results || data || [];

      console.log(
        `Fetched ${permissions.length} permissions for user ${userItem.username}:`,
        permissions,
      );

      // Extract species IDs from permissions
      const assignedSpeciesIds = permissions.map((p) => {
        if (typeof p.specie === "object") {
          return p.specie.id;
        }
        return p.specie;
      });

      console.log("Assigned species IDs for this user:", assignedSpeciesIds);
      setSelectedSpecies(assignedSpeciesIds);
      setPermissionsDialogOpen(true);
    } catch (err) {
      console.error("Error fetching user species permissions:", err);
      setError("Failed to load species permissions");
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Handle Organization Species Permissions (for organization admins)
  const handleOpenOrgPermissionsDialog = async (organization) => {
    setOrgForPermissions(organization);
    setOrgPermissionsLoading(true);

    try {
      const headers = { Authorization: `Token ${token}` };

      console.log(
        `Opening permissions dialog for organization: ${organization.id} - ${organization.name}`,
      );

      // Fetch current permissions for this specific organization
      const response = await fetch(
        `${API_BASE_URL}/core/api/organization-species-permissions/?organization=${organization.id}`,
        { headers },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch organization permissions");
      }

      const data = await response.json();
      const permissions = data.results || data || [];

      console.log(
        `Fetched ${permissions.length} permissions for organization ${organization.name}:`,
        permissions,
      );

      // Extract species IDs from permissions
      const assignedSpeciesIds = permissions.map((p) => {
        if (typeof p.specie === "object") {
          return p.specie.id;
        }
        return p.specie;
      });

      console.log("Assigned organization species IDs:", assignedSpeciesIds);
      setSelectedOrgSpecies(assignedSpeciesIds);
      setOrgPermissionsDialogOpen(true);
    } catch (err) {
      console.error("Error fetching organization species permissions:", err);
      setError("Failed to load organization species permissions");
    } finally {
      setOrgPermissionsLoading(false);
    }
  };

  // Save User Species Permissions (for regular users)
  const handleSaveUserPermissions = async () => {
    if (!userForPermissions) return;

    setPermissionsLoading(true);
    try {
      const headers = {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      };

      console.log(
        `Saving permissions for user: ${userForPermissions.id} - ${userForPermissions.username}`,
      );

      // Get current permissions for THIS SPECIFIC USER ONLY
      const currentRes = await fetch(
        `${API_BASE_URL}/core/api/user-species-permissions/?user=${userForPermissions.id}`,
        { headers },
      );

      if (!currentRes.ok) {
        throw new Error("Failed to fetch current permissions");
      }

      const currentData = await currentRes.json();
      const currentPermissions = currentData.results || currentData || [];

      console.log("Current permissions for this user:", currentPermissions);
      console.log("Selected species for this user:", selectedSpecies);

      // Double-check that all currentPermissions belong to this user
      const validCurrentPermissions = currentPermissions.filter(
        (perm) =>
          perm.user === userForPermissions.id ||
          (typeof perm.user === "object" &&
            perm.user.id === userForPermissions.id),
      );

      if (validCurrentPermissions.length !== currentPermissions.length) {
        console.warn("Found permissions for other users, filtering out");
      }

      // Delete permissions that are no longer selected - but only for this user
      const permissionsToDelete = validCurrentPermissions.filter((perm) => {
        const specieId =
          typeof perm.specie === "object" ? perm.specie.id : perm.specie;
        return !selectedSpecies.includes(specieId);
      });

      for (const perm of permissionsToDelete) {
        console.log(
          `Deleting user permission ${perm.id} for user ${userForPermissions.id}`,
        );
        const deleteRes = await fetch(
          `${API_BASE_URL}/core/api/user-species-permissions/${perm.id}/`,
          {
            method: "DELETE",
            headers,
          },
        );

        if (!deleteRes.ok) {
          console.error(`Failed to delete permission ${perm.id}`);
          const errorData = await deleteRes.json().catch(() => ({}));
          throw new Error(
            errorData.detail || `Failed to delete permission ${perm.id}`,
          );
        }
      }

      // Get the list of species that are already assigned
      const existingSpecies = validCurrentPermissions.map((p) =>
        typeof p.specie === "object" ? p.specie.id : p.specie,
      );

      // Add new permissions
      const speciesToAdd = selectedSpecies.filter(
        (specieId) => !existingSpecies.includes(specieId),
      );

      for (const specieId of speciesToAdd) {
        console.log(
          `Creating permission for user ${userForPermissions.id} and specie ${specieId}`,
        );

        const createRes = await fetch(
          `${API_BASE_URL}/core/api/user-species-permissions/`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              user: userForPermissions.id,
              specie: specieId,
            }),
          },
        );

        if (!createRes.ok) {
          const errorData = await createRes.json().catch(() => ({}));
          console.error("Create permission error:", errorData);

          if (createRes.status === 403) {
            throw new Error(
              "You don't have permission to assign this species.",
            );
          } else {
            throw new Error(
              errorData.detail ||
                `Failed to create permission for species ID ${specieId}`,
            );
          }
        }
      }

      setSuccess(
        `User species permissions updated successfully for ${userForPermissions.username}`,
      );
      setPermissionsDialogOpen(false);
      setUserForPermissions(null);
      setSelectedSpecies([]);

      await fetchData();
    } catch (err) {
      console.error("Error saving user species permissions:", err);
      setError(err.message || "Failed to update species permissions");
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Save Organization Species Permissions (for organization admins)
  const handleSaveOrgPermissions = async () => {
    if (!orgForPermissions) return;

    setOrgPermissionsLoading(true);
    try {
      const headers = {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      };

      console.log(
        `Saving permissions for organization: ${orgForPermissions.id} - ${orgForPermissions.name}`,
      );

      // Get current permissions for THIS SPECIFIC ORGANIZATION ONLY
      const currentRes = await fetch(
        `${API_BASE_URL}/core/api/organization-species-permissions/?organization=${orgForPermissions.id}`,
        { headers },
      );

      if (!currentRes.ok) {
        throw new Error("Failed to fetch current permissions");
      }

      const currentData = await currentRes.json();
      const currentPermissions = currentData.results || currentData || [];

      console.log(
        "Current org permissions for this organization:",
        currentPermissions,
      );
      console.log("Selected org species:", selectedOrgSpecies);

      // Double-check that all currentPermissions belong to this organization
      const validCurrentPermissions = currentPermissions.filter(
        (perm) =>
          perm.organization === orgForPermissions.id ||
          (typeof perm.organization === "object" &&
            perm.organization.id === orgForPermissions.id),
      );

      if (validCurrentPermissions.length !== currentPermissions.length) {
        console.warn(
          "Found permissions for other organizations, filtering out",
        );
      }

      // Delete permissions that are no longer selected - but only for this organization
      const permissionsToDelete = validCurrentPermissions.filter((perm) => {
        const specieId =
          typeof perm.specie === "object" ? perm.specie.id : perm.specie;
        return !selectedOrgSpecies.includes(specieId);
      });

      for (const perm of permissionsToDelete) {
        console.log(
          `Deleting org permission ${perm.id} for organization ${orgForPermissions.id}`,
        );
        const deleteRes = await fetch(
          `${API_BASE_URL}/core/api/organization-species-permissions/${perm.id}/`,
          {
            method: "DELETE",
            headers,
          },
        );

        if (!deleteRes.ok) {
          console.error(`Failed to delete permission ${perm.id}`);
          const errorData = await deleteRes.json().catch(() => ({}));
          throw new Error(
            errorData.detail || `Failed to delete permission ${perm.id}`,
          );
        }
      }

      // Get the list of species that are already assigned
      const existingSpecies = validCurrentPermissions.map((p) =>
        typeof p.specie === "object" ? p.specie.id : p.specie,
      );

      // Add new permissions
      const speciesToAdd = selectedOrgSpecies.filter(
        (specieId) => !existingSpecies.includes(specieId),
      );

      for (const specieId of speciesToAdd) {
        console.log(
          `Creating org permission for organization ${orgForPermissions.id} and specie ${specieId}`,
        );

        const createRes = await fetch(
          `${API_BASE_URL}/core/api/organization-species-permissions/`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              organization: orgForPermissions.id,
              specie: specieId,
            }),
          },
        );

        if (!createRes.ok) {
          const errorData = await createRes.json().catch(() => ({}));
          console.error("Create org permission error:", errorData);
          throw new Error(
            errorData.detail ||
              `Failed to create organization permission for species ID ${specieId}`,
          );
        }
      }

      setSuccess(
        `Organization species permissions updated successfully for ${orgForPermissions.name}`,
      );
      setOrgPermissionsDialogOpen(false);
      setOrgForPermissions(null);
      setSelectedOrgSpecies([]);

      await fetchData();
    } catch (err) {
      console.error("Error saving organization species permissions:", err);
      setError(err.message || "Failed to update organization permissions");
    } finally {
      setOrgPermissionsLoading(false);
    }
  };

  // Handle server-side pagination navigation
  const handleServerPageChange = (direction) => {
    if (direction === "next" && nextPage) {
      fetchData(nextPage);
    } else if (direction === "prev" && previousPage) {
      fetchData(previousPage);
    }
  };

  // Get organization display name
  const getOrganizationName = (org) => {
    if (!org) return "No organization";
    return typeof org === "object" ? org.name : "Organization";
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header and Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                value={userTypeFilter}
                label="User Type"
                onChange={(e) => setUserTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="admin">System Admin</MenuItem>
                <MenuItem value="superuser">Organization Admin</MenuItem>
                <MenuItem value="user">Regular User</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {user?.user_type === "admin" && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={orgFilter}
                  label="Organization"
                  onChange={(e) => setOrgFilter(e.target.value)}
                >
                  <MenuItem value="all">All Organizations</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchData()}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>

        {/* Server-side pagination info */}
        {user?.user_type === "admin" && (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {users.length} of {totalUsers} total users
            </Typography>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleServerPageChange("prev")}
                disabled={!previousPage || loading}
              >
                Previous Page
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleServerPageChange("next")}
                disabled={!nextPage || loading}
              >
                Next Page
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Rest of the component remains the same... */}
      {/* Users Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((userItem) => (
                  <TableRow
                    key={userItem.id}
                    hover
                    sx={{
                      opacity: userItem.is_active ? 1 : 0.6,
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          {userItem.first_name?.[0] ||
                            userItem.username?.[0] ||
                            "U"}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {userItem.first_name} {userItem.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{userItem.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{userItem.email}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getUserTypeIcon(userItem.user_type)}
                        label={userItem.user_type_display || userItem.user_type}
                        color={getUserTypeColor(userItem.user_type)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {userItem.organization ? (
                        <Chip
                          icon={<BusinessIcon />}
                          label={getOrganizationName(userItem.organization)}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No organization
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          userItem.is_active ? (
                            <CheckCircleIcon />
                          ) : (
                            <CancelIcon />
                          )
                        }
                        label={userItem.is_active ? "Active" : "Inactive"}
                        color={userItem.is_active ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(userItem.date_created).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditDialog(userItem)}
                              disabled={userItem.id === user?.id}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip
                          title={userItem.is_active ? "Deactivate" : "Activate"}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleUserStatus(userItem)}
                              disabled={userItem.id === user?.id}
                            >
                              {userItem.is_active ? (
                                <LockOpenIcon fontSize="small" />
                              ) : (
                                <LockIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* For System Admin: Show organization permissions button for organization admins */}
                        {user?.user_type === "admin" &&
                          userItem.user_type === "superuser" &&
                          userItem.organization && (
                            <Tooltip title="Assign Species to Organization">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleOpenOrgPermissionsDialog(
                                      userItem.organization,
                                    )
                                  }
                                >
                                  <BusinessIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                        {/* For Organization Admin: Show user permissions button for regular users */}
                        {(user?.user_type === "admin" ||
                          (user?.user_type === "superuser" &&
                            userItem.user_type === "user" &&
                            userItem.organization?.id ===
                              user?.organization?.id)) && (
                          <Tooltip title="Assign Species to User">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleOpenUserPermissionsDialog(userItem)
                                }
                              >
                                <SpeciesIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}

                        {user?.user_type === "admin" &&
                          userItem.id !== user?.id && (
                            <Tooltip title="Delete">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteUser(userItem.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                value={editForm.username || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, username: e.target.value })
                }
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editForm.email || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editForm.first_name || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, first_name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editForm.last_name || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, last_name: e.target.value })
                }
              />
            </Grid>

            {user?.user_type === "admin" && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>User Type</InputLabel>
                    <Select
                      value={editForm.user_type || ""}
                      label="User Type"
                      onChange={(e) =>
                        setEditForm({ ...editForm, user_type: e.target.value })
                      }
                    >
                      <MenuItem value="admin">System Administrator</MenuItem>
                      <MenuItem value="superuser">
                        Organization Administrator
                      </MenuItem>
                      <MenuItem value="user">Regular User</MenuItem>
                      <MenuItem value="viewer">Viewer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Organization</InputLabel>
                    <Select
                      value={editForm.organization || ""}
                      label="Organization"
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          organization: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="">No Organization</MenuItem>
                      {organizations.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.is_active ?? true}
                    onChange={(e) =>
                      setEditForm({ ...editForm, is_active: e.target.checked })
                    }
                  />
                }
                label="Active Account"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={20} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Species Permissions Dialog */}
      <Dialog
        open={permissionsDialogOpen}
        onClose={() => {
          setPermissionsDialogOpen(false);
          setUserForPermissions(null);
          setSelectedSpecies([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assign Species to User: {userForPermissions?.username}
        </DialogTitle>
        <DialogContent>
          {permissionsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                {user?.user_type === "superuser"
                  ? "Select which species this user can access. You can only assign species that your organization has permission for."
                  : "Select which species this user can access and view in the system."}
              </Typography>

              {user?.user_type === "superuser" &&
                availableSpecies.length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Your organization doesn't have any species assigned. Please
                    contact your system administrator to assign species to your
                    organization first.
                  </Alert>
                )}

              <Box sx={{ maxHeight: 400, overflow: "auto", mt: 2 }}>
                {availableSpecies.length > 0 ? (
                  availableSpecies.map((specie) => (
                    <Box
                      key={specie.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        mb: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: selectedSpecies.includes(specie.id)
                          ? "action.selected"
                          : "background.paper",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            backgroundColor: specie.color || "#1976d2",
                          }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {specie.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {specie.id}
                          </Typography>
                        </Box>
                        {specie.endangered && (
                          <Chip label="Endangered" color="error" size="small" />
                        )}
                        {specie.enabled === false && (
                          <Chip label="Disabled" color="default" size="small" />
                        )}
                      </Box>
                      <Switch
                        checked={selectedSpecies.includes(specie.id) || false}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSpecies([...selectedSpecies, specie.id]);
                          } else {
                            setSelectedSpecies(
                              selectedSpecies.filter((id) => id !== specie.id),
                            );
                          }
                        }}
                        disabled={specie.enabled === false}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}
                  >
                    {user?.user_type === "superuser"
                      ? "No species have been assigned to your organization yet. Contact your system administrator."
                      : "No species available to assign. Contact your system administrator."}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPermissionsDialogOpen(false);
              setUserForPermissions(null);
              setSelectedSpecies([]);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveUserPermissions}
            variant="contained"
            disabled={permissionsLoading || availableSpecies.length === 0}
          >
            {permissionsLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Save Permissions"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Organization Species Permissions Dialog */}
      <Dialog
        open={orgPermissionsDialogOpen}
        onClose={() => {
          setOrgPermissionsDialogOpen(false);
          setOrgForPermissions(null);
          setSelectedOrgSpecies([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Assign Species to Organization: {orgForPermissions?.name}
        </DialogTitle>
        <DialogContent>
          {orgPermissionsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" paragraph>
                Select which species this organization can access. Organization
                admins will be able to assign these species to users in their
                organization.
              </Typography>

              <Box sx={{ maxHeight: 400, overflow: "auto", mt: 2 }}>
                {availableOrgSpecies.length > 0 ? (
                  availableOrgSpecies.map((specie) => (
                    <Box
                      key={specie.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 2,
                        mb: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: selectedOrgSpecies.includes(specie.id)
                          ? "action.selected"
                          : "background.paper",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            backgroundColor: specie.color || "#1976d2",
                          }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {specie.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {specie.id}
                          </Typography>
                        </Box>
                        {specie.endangered && (
                          <Chip label="Endangered" color="error" size="small" />
                        )}
                        {specie.enabled === false && (
                          <Chip label="Disabled" color="default" size="small" />
                        )}
                      </Box>
                      <Switch
                        checked={
                          selectedOrgSpecies.includes(specie.id) || false
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedOrgSpecies([
                              ...selectedOrgSpecies,
                              specie.id,
                            ]);
                          } else {
                            setSelectedOrgSpecies(
                              selectedOrgSpecies.filter(
                                (id) => id !== specie.id,
                              ),
                            );
                          }
                        }}
                        disabled={specie.enabled === false}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}
                  >
                    No species available to assign.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOrgPermissionsDialogOpen(false);
              setOrgForPermissions(null);
              setSelectedOrgSpecies([]);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveOrgPermissions}
            variant="contained"
            disabled={orgPermissionsLoading || availableOrgSpecies.length === 0}
          >
            {orgPermissionsLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Save Permissions"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars for notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}
